import { Container } from "@material-ui/core";
import React, { useRef, useState } from "react";
import PageTitle from "../../components/pageTitle/PageTitle";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { CreditCard, Eye, Plus, Save, Send, Trash2, Wand2, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../components/ui/utils";
import { useToast } from "../../hooks/useToast";
import { Textarea } from "../../components/ui/textArea";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useMediaQuery } from "@mui/material";

const InvoiceCreate = () => {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientCc, setClientCc] = useState("");
  const [clientBcc, setClientBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [clientAddress, setClientAddress] = useState("");
  const [shipToAddress, setShipToAddress] = useState("");
  const [shipFromAddress, setShipFromAddress] = useState(
    "47 Woodland Ave, Merlotint, NJ, 07042, USA"
  );
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [attachToEmail, setAttachToEmail] = useState(true);
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);
  const [tags, setTags] = useState(["Light"]);
  const [taxRate, setTaxRate] = useState(0);
  const [newTag, setNewTag] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [shippingDate, setShippingDate] = useState(new Date());
  const [dueDate, setDueDate] = useState();
  const [showReviewSend, setShowReviewSend] = useState(false);
  const [dueTerm, setDueTerm] = useState("30");
  const [reviewCcEmails, setReviewCcEmails] = useState([]);
  const [reviewBccEmails, setReviewBccEmails] = useState([]);
  const [showReviewCcBcc, setShowReviewCcBcc] = useState(false);
  const [notes, setNotes] = useState(
    "We appreciate your business and look forward to helping you again soon."
  );
  const [memo, setMemo] = useState(
    "This memo will not show up on your invoice, but will appear on the statement."
  );
  const [paymentOptions, setPaymentOptions] = useState({
    credit: true,
    ach: false,
    applePay: false,
    paypal: false,
    googlePay: false,
  });
  const isMobile = useMediaQuery('(max-width:767px)')

  const [items, setItems] = useState([
    {
      id: "1",
      serviceDate: "",
      productService: "",
      description: "",
      quantity: 1,
      rate: 0,
      tax: false,
    },
  ]);
  const { toast } = useToast();

  const ClientOptions = [
    { label: "Bogopa Service Corp", value: "Bogopa Service Corp" },
    { label: "Mr. Skaurk", value: "Mr. Skaurk" },
    { label: "ABC Corp", value: "ABC Corp" },
    { label: "XYZ Ltd", value: "XYZ Ltd" },
  ];

  const TaxOptions = [
    { label: "Tax Exempt", value: "0" },
    { label: "Automatic Calculation (8.875%)", value: "8.875" },
    { label: "5%", value: "5" },
    { label: "7.5%", value: "7.5" },
    { label: "10%", value: "10" },
  ];

  const TermOptions = [
    { label: "Net 30", value: "30" },
    { label: "Net 60", value: "60" },
    { label: "Net 90", value: "90" },
  ];

  const availableServices = [
    {
      id: "consulting",
      name: "Business Consulting",
      description:
        "Strategic business consulting services to help optimize operations and growth strategies",
      rate: 150,
    },
    {
      id: "design",
      name: "UI/UX Design",
      description:
        "User interface and experience design services including wireframes, prototypes, and visual design",
      rate: 500,
    },
    {
      id: "development",
      name: "Web Development",
      description:
        "Custom web application development using modern technologies and best practices",
      rate: 125,
    },
    {
      id: "marketing",
      name: "Digital Marketing",
      description:
        "Comprehensive digital marketing strategy including SEO, social media, and content marketing",
      rate: 100,
    },
    {
      id: "maintenance",
      name: "System Maintenance",
      description:
        "Regular system maintenance, updates, and technical support services",
      rate: 85,
    },
    {
      id: "training",
      name: "Staff Training",
      description:
        "Professional training sessions for staff development and skill enhancement",
      rate: 200,
    },
  ];

  const handleClientSelect = (clientName) => {
    setClientName(clientName);
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now().toString(),
      serviceDate: "",
      productService: "",
      description: "",
      quantity: 1,
      rate: 0,
      tax: false,
    };
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems(
      items.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleServiceSelect = (itemId, serviceId) => {
    const service = availableServices.find((s) => s.id === serviceId);
    if (service) {
      setItems(
        items.map((item) =>
          item.id === itemId
            ? {
                ...item,
                productService: service.name,
                description: service.description,
                rate: service.rate,
              }
            : item
        )
      );
    }
  };

  const generateDescription = async (itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (!item?.productService) return;

    // Simulate AI description generation
    const enhancedDescriptions = {
      "Business Consulting":
        "Comprehensive business consulting services including strategic planning, operational optimization, market analysis, and growth strategy development tailored to your specific business needs.",
      "UI/UX Design":
        "Professional user interface and user experience design services covering user research, wireframing, prototyping, visual design, and usability testing to create intuitive digital experiences.",
      "Web Development":
        "Full-stack web development services using cutting-edge technologies, including responsive design, database integration, API development, and performance optimization.",
      "Digital Marketing":
        "Complete digital marketing solutions encompassing SEO optimization, social media strategy, content marketing, email campaigns, and analytics-driven growth strategies.",
      "System Maintenance":
        "Comprehensive system maintenance and support services including regular updates, security monitoring, performance optimization, backup management, and technical troubleshooting.",
      "Staff Training":
        "Professional staff development and training programs designed to enhance team skills, improve productivity, and ensure successful adoption of new systems and processes.",
    };

    const newDescription =
      enhancedDescriptions[item.productService] ||
      `Professional ${item.productService.toLowerCase()} services tailored to meet your business requirements with expert delivery and ongoing support.`;

    setItems(
      items.map((i) =>
        i.id === itemId ? { ...i, description: newDescription } : i
      )
    );

    toast({
      title: "Description Generated",
      description: "AI-enhanced description has been applied.",
    });
  };

  const handlePaymentOptionChange = (option, checked) => {
    setPaymentOptions((prev) => ({
      ...prev,
      [option]: checked,
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const calculateSubtotal = () => {
    return items.reduce((total, item) => total + item.quantity * item.rate, 0);
  };

  const calculateTaxAmount = () => {
    const subtotal = calculateSubtotal();
    return subtotal * (taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxAmount();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setAttachment(file);
    } else {
      alert("Please select a valid image file.");
    }
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
  };
  
  const handleChooseImage = () => {
    fileInputRef.current?.click();
  };
  
  const validateForm = () => {
    const errors = [];
    if (!clientName || clientName === "new") {
      errors.push("Please select a customer");
    }
    if (!invoiceNumber.trim()) {
      errors.push("Invoice number is required");
    }
    if (items.some(item => !item.productService)) {
      errors.push("All items must have a product/service selected");
    }
    if (items.some(item => item.quantity <= 0)) {
      errors.push("All items must have a quantity greater than 0");
    }
    if (items.some(item => item.rate <= 0)) {
      errors.push("All items must have a rate greater than 0");
    }
    return errors;
  };

  const handleSubmit = (e, submitType) => {
    e.preventDefault();
    if (submitType === "issued") {
      const errors = validateForm();
      if (errors.length > 0) {
        toast({
          title: "Validation Error",
          description: errors[0],
          variant: "destructive"
        });
        return;
      }
      setShowReviewSend(true);
    } else {
      toast({
        title: "Invoice Saved as Draft",
        description: "Your invoice has been saved as a draft."
      });
    }
  };

  return (
    <Container className="relative">
      <PageTitle
        pageTitle="Create Invoice"
        showBreadcrumb={[
          { label: "Home", to: "/" },
          { label: "Invoices", to: "/invoices/manage" },
        ]}
        BreadCrumbCurrentPageTitle={"Invoice"}
      />
      <div className="max-w-7xl mx-auto p-16 sm:p-6 lg:p-8">
        {/* Header with Balance */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-primary">
            INVOICE
          </h1>
          <div className="text-left sm:text-right">
            <div className="text-sm text-muted-foreground">
              Balance due (USD)
            </div>
            <div className="text-2xl sm:text-3xl font-bold">$0</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <SingleSelectNew
                value={clientName}
                onChange={handleClientSelect}
                options={ClientOptions}
                className="w-full max-w-[240px]"
              />
            </div>
            <div>
              <Input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="sam.Yun@bogopausa.com"
                className="w-full max-w-[240px]"
              />
            </div>
            <div>
              <Button
                variant="link"
                className="p-0 h-auto text-sm text-primary"
                onClick={() => setShowCcBcc(!showCcBcc)}
              >
                Cc/Bcc
              </Button>
              {showCcBcc && (
                <div className="mt- space-y-2">
                  <Input
                    type="email"
                    value={clientCc}
                    onChange={(e) => setClientCc(e.target.value)}
                    placeholder="Cc"
                    className="w-full max-w-[240px]"
                  />
                  <Input
                    type="email"
                    value={clientBcc}
                    onChange={(e) => setClientBcc(e.target.value)}
                    placeholder="Bcc"
                    className="w-full max-w-[240px]"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="d-flex flex-space-between">
              <div className="w-[200px]">
              <Label className="text-sm font-medium">Shipping via</Label>
              <Input
                type="text"
                placeholder="FedEx, UPS, USPS..."
                className="mt-4 w-full max-w-[200px]"
                />
              </div>
              <div className="w-[200px]">
              <Label className="text-sm font-medium">Invoice no.</Label>
              <Input
                type="text"
                placeholder="Invoice No."
                className="mt-4 w-full max-w-[200px]"
                onChange={(e) => setInvoiceNumber(e.target.value)}
              />
            </div>
            </div>

            <div className="d-flex flex-space-between">
              <div className="w-[200px]">
              <Label className="text-sm font-medium">Shipping Date</Label>
              <Input type="date" className="mt-4 w-full max-w-[240px]" onChange={(e) => setShippingDate(e.target.value)}/>
              </div>
              <div className="w-[200px]">
              <Label className="text-sm font-medium">Invoice Date</Label>
              <Input type="date" className="mt-4 w-[240px] w-full max-w-[240px]" onChange={(e) => setInvoiceDate(e.target.value)}/>
              </div>
            </div>

            <div className="d-flex flex-space-between">
              <div className="w-[200px]">
              <Label className="text-sm font-medium">Tracking No.</Label>
              <Input
                type="text"
                placeholder="Tracking number"
                className="mt-4 w-full w-[200px] max-w-[240px]"
                onChange={(e) => setTrackingNo(e.target.value)}
              />
              </div>
              <div className="w-[200px]">
              <Label className="text-sm font-medium">Due Date</Label>
              <Input type="date" className="mt-4 w-[240px] w-full max-w-[240px]" onChange={(e) => setDueDate(e.target.value)}/>
                </div>
            </div>

            <div className="d-flex flex-space-between">
              <div className="w-[200px]"></div>
              <div>
              <Label className="text-sm font-medium">Terms</Label>
              <SingleSelectNew
                value={dueTerm}
                placeholder="Terms"
                className="mt-4 w-full w-[200px] max-w-[240px]"
                onChange={(e) => setDueTerm(e)}
                options={TermOptions}
                />
                </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-20">
          {/* Bill To */}
          <div>
            <Label className="text-sm font-medium">Bill to</Label>
            <Textarea
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
              placeholder="Sam Yun&#10;Bogopa Service Corp&#10;33-02 Skillman Ave&#10;5th Floor"
              className="mt-2 min-h-[120px] resize-none"
            />
            <Button
              variant="link"
              className="p-0 h-auto text-sm text-primary mt-4"
            >
              Edit Customer
            </Button>
          </div>

          {/* Ship To */}
          <div>
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Ship to</Label>
              <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
              </div>
            </div>
            <Textarea
              value={shipToAddress}
              onChange={(e) => setShipToAddress(e.target.value)}
              placeholder="Sam Yun&#10;Bogopa Service Corp&#10;33-02 Skillman Ave&#10;5th Floor"
              className="mt-2 min-h-[120px] resize-none"
            />
            <Button
              variant="link"
              className="p-0 h-auto text-sm text-primary mt-4"
            >
              Remove shipping info
            </Button>
          </div>

          {/* Ship From */}
          <div>
            <Label className="text-sm font-medium">Ship from (hidden)</Label>
            <Textarea
              value={shipFromAddress}
              onChange={(e) => setShipFromAddress(e.target.value)}
              placeholder="47 Woodland Ave, Montclair,&#10;NJ, 07042, USA"
              className="mt-2 min-h-[120px] resize-none bg-muted/30"
              readOnly
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-20">
          <span className="text-sm text-muted-foreground">Tags:</span>
          {tags.map((tag, index) => (
            <Badge
              key={index}
              variant="secondaryNew"
              className="flex items-center gap-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="text-muted-foreground hover:text-foreground ml-4"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <div className="flex items-center gap-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), handleAddTag())
              }
              placeholder="Add tag"
              className="w-24 text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddTag}
              className="px-2"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <div className={isMobile ? "bg-card border border-border p-10 mt-20" :"bg-card border border-border p-24 mt-20"}>
          <div className="flex items-center justify-between pb-16 border-b border-border">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-card-foreground">
                Products & Services
              </h2>
              <Badge variant="secondaryNew" className="text-xs">
                {items.length} item{items.length !== 1 ? "s" : ""}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddItem}
              className="flex items-center gap-2 hover:bg-primary hover:text-primary-foreground"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>

          {/* Items List */}
          <div className="space-y-6 mt-24">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="bg-muted/20 rounded-lg p-16 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-card-foreground">
                    Item #{index + 1}
                  </h3>
                  <div className="text-sm font-medium text-muted-foreground">
                    Total: ${(item.quantity * item.rate).toFixed(2)}
                  </div>
                </div>

                {/* Mobile/Tablet Enhanced Layout */}
                <div className="block md:hidden">
                  <div className="bg-card border border-border rounded-lg p-16 sm:p-6 space-y-6 shadow-sm">
                    {/* Service & AI Enhancement Row */}
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-semibold text-foreground mb-12 block">
                          Product/Service *
                        </Label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <Select
                            value={item.productService}
                            onValueChange={(value) => {
                              handleItemChange(
                                item.id,
                                "productService",
                                value
                              );
                              handleServiceSelect(
                                item.id,
                                availableServices.find((s) => s.name === value)
                                  ?.id || ""
                              );
                            }}
                          >
                            <SelectTrigger className="flex-1 h-12 bg-background border-border text-left text-base">
                              <SelectValue placeholder="Select service..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableServices.map((service) => (
                                <SelectItem
                                  key={service.id}
                                  value={service.name}
                                >
                                  {service.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => generateDescription(item.id)}
                            disabled={!item.productService}
                            className="h-12 w-full sm:w-12 flex items-center justify-center gap-2 bg-primary/5 border-primary/20 hover:bg-primary/10"
                            title="Generate AI description"
                          >
                            <Wand2 className="h-4 w-4 text-primary" />
                            <span className="sm:hidden">
                              Generate Description
                            </span>
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-foreground mb-12 block">
                          Service Date
                        </Label>
                        <Input
                          type="date"
                          value={item.serviceDate}
                          onChange={(e) =>
                            handleItemChange(
                              item.id,
                              "serviceDate",
                              e.target.value
                            )
                          }
                          className="h-12 bg-background border-border text-base"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <Label className="text-sm font-semibold text-foreground mb-12 block">
                        Description
                      </Label>
                      <Textarea
                        value={item.description}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Detailed service description..."
                        className="min-h-[100px] resize-none bg-background border-border text-base leading-relaxed p-16"
                      />
                    </div>

                    {/* Pricing & Tax Section */}
                    <div className="bg-muted/20 rounded-lg p-16 space-y-4">
                      <h4 className="text-sm font-semibold text-foreground">
                        Pricing Details
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold text-foreground mb-8 block">
                            Quantity
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "quantity",
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="h-12 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-center font-semibold bg-background text-base"
                          />
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-foreground mb-8 block">
                            Rate 
                          </Label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-base text-muted-foreground font-semibold">
                              $
                            </span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "rate",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="h-12 pl-32 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none font-semibold bg-background text-base"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Tax and Total */}
                      <div className="flex items-center justify-between pt-16 border-t border-border/50">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id={`tax-${item.id}`}
                            checked={item.tax}
                            onCheckedChange={(checked) =>
                              handleItemChange(item.id, "tax", checked === true)
                            }
                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary w-5 h-5"
                          />
                          <Label
                            htmlFor={`tax-${item.id}`}
                            className="text-sm font-semibold cursor-pointer"
                          >
                            Apply Tax
                          </Label>
                        </div>

                        <div className="text-right">
                          <div className="text-xl font-bold text-primary">
                            ${(item.quantity * item.rate).toFixed(2)}
                          </div>
                          <div className="text-xs text-muted-foreground font-medium">
                            Item Total
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end pt-8">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={items.length === 1}
                        className="h-10 px-4 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        title="Remove item"
                      >
                        <Trash2 className="h-4 w-4 mr-8" />
                        Remove Item
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 items-end">
                  {/* Service Date */}
                  <div className="md:col-span-2">
                    <Label className="block text-sm font-semibold text-card-foreground mb-8">
                      Service Date
                    </Label>
                    <Input
                      type="date"
                      value={item.serviceDate}
                      onChange={(e) =>
                        handleItemChange(item.id, "serviceDate", e.target.value)
                      }
                      className="w-full"
                    />
                  </div>

                  {/* Product/Service */}
                  <div className="md:col-span-3">
                    <Label className="block text-sm font-semibold text-card-foreground mb-8">
                      Product/Service
                    </Label>
                    <Select
                      value={item.productService}
                      onValueChange={(value) => {
                        handleItemChange(item.id, "productService", value);
                        handleServiceSelect(
                          item.id,
                          availableServices.find((s) => s.name === value)?.id ||
                            ""
                        );
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select service..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableServices.map((service) => (
                          <SelectItem key={service.id} value={service.name}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div className="md:col-span-3">
                    <Label className="text-sm font-semibold text-card-foreground mb-8">
                      Description
                    </Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) =>
                        handleItemChange(item.id, "description", e.target.value)
                      }
                      placeholder="Detailed service description..."
                      className="min-h-[60px] resize-y"
                    />
                  </div>

                  {/* Quantity */}
                  <div className="md:col-span-1">
                    <Label className="block text-sm font-semibold text-card-foreground mb-8">
                      Quantity
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          item.id,
                          "quantity",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-center font-medium"
                    />
                  </div>

                  {/* Rate */}
                  <div className="md:col-span-1">
                    <Label className="block text-sm font-semibold text-card-foreground mb-8">
                      Rate
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) =>
                          handleItemChange(
                            item.id,
                            "rate",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="pl-24 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none font-medium"
                      />
                    </div>
                  </div>

                  {/* Tax */}
                  <div className="md:col-span-1 flex flex-col items-center">
                    <Label className="text-sm font-semibold text-card-foreground mb-8">
                      Tax
                    </Label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`tax-${item.id}`}
                        checked={item.tax}
                        onCheckedChange={(checked) =>
                          handleItemChange(item.id, "tax", checked === true)
                        }
                      />
                      <Label htmlFor={`tax-${item.id}`} className="text-sm">
                        Taxable
                      </Label>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-1 flex flex-col items-center">
                    <Label className="text-sm font-semibold text-card-foreground mb-8 opacity-0">
                      Actions
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={items.length === 1}
                      className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                      title="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-20">
          {/* Left Side - Invoice Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Customer Payment Options */}
            <div className="bg-card border border-border rounded-lg p-24">
              <div className="flex items-center justify-between pb-16 border-b border-border">
                <h2 className="text-lg font-semibold text-card-foreground">
                  Payment Options
                </h2>
                <Button
                  variant="link"
                  className="p-0 h-auto text-sm text-primary hover:text-primary/80"
                >
                  Edit Options
                </Button>
              </div>

              {/* Payment Method Options - Enhanced Mobile Layout */}
              <div className="space-y-4 mt-24">
                {/* Mobile & Tablet Layout */}
                <div className="md:hidden space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <div
                      className={cn(
                        "flex items-center justify-between p-16 rounded-lg border-2 transition-all cursor-pointer touch-manipulation",
                        paymentOptions.ach
                          ? "bg-primary/5 border-primary/30 shadow-sm"
                          : "bg-card border-border hover:border-border/60"
                      )}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <Label
                            htmlFor="bank-mobile"
                            className="text-sm font-semibold text-foreground block"
                          >
                            Bank Transfer
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            ACH / eCheck
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        id="bank-mobile"
                        checked={paymentOptions.ach}
                        onCheckedChange={(checked) =>
                          handlePaymentOptionChange("ach", checked === true)
                        }
                        className="w-5 h-5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </div>

                    <div
                      className={cn(
                        "flex items-center justify-between p-16 rounded-lg border-2 transition-all cursor-pointer touch-manipulation",
                        paymentOptions.credit
                          ? "bg-primary/5 border-primary/30 shadow-sm"
                          : "bg-card border-border hover:border-border/60"
                      )}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <Label
                            htmlFor="credit-mobile"
                            className="text-sm font-semibold text-foreground block"
                          >
                            Credit Card
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Visa, Mastercard, Amex
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        id="credit-mobile"
                        checked={paymentOptions.credit}
                        onCheckedChange={(checked) =>
                          handlePaymentOptionChange("credit", checked === true)
                        }
                        className="w-5 h-5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </div>

                    <div
                      className={cn(
                        "flex items-center justify-between p-16 rounded-lg border-2 transition-all cursor-pointer touch-manipulation",
                        paymentOptions.applePay
                          ? "bg-primary/5 border-primary/30 shadow-sm"
                          : "bg-card border-border hover:border-border/60"
                      )}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                          <div className="w-5 h-5 bg-black rounded text-white text-xs flex items-center justify-center font-bold">
                            A
                          </div>
                        </div>
                        <div>
                          <Label
                            htmlFor="applePay-mobile"
                            className="text-sm font-semibold text-foreground block"
                          >
                            Apple Pay
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Fast & secure
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        id="applePay-mobile"
                        checked={paymentOptions.applePay}
                        onCheckedChange={(checked) =>
                          handlePaymentOptionChange(
                            "applePay",
                            checked === true
                          )
                        }
                        className="w-5 h-5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </div>

                    <div
                      className={cn(
                        "flex items-center justify-between p-16 rounded-lg border-2 transition-all cursor-pointer touch-manipulation",
                        paymentOptions.googlePay
                          ? "bg-primary/5 border-primary/30 shadow-sm"
                          : "bg-card border-border hover:border-border/60"
                      )}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                          <div className="w-5 h-5 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-bold">
                            G
                          </div>
                        </div>
                        <div>
                          <Label
                            htmlFor="googlePay-mobile"
                            className="text-sm font-semibold text-foreground block"
                          >
                            Google Pay
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Quick checkout
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        id="googlePay-mobile"
                        checked={paymentOptions.googlePay}
                        onCheckedChange={(checked) =>
                          handlePaymentOptionChange(
                            "googlePay",
                            checked === true
                          )
                        }
                        className="w-5 h-5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </div>

                    <div
                      className={cn(
                        "flex items-center justify-between p-16 rounded-lg border-2 transition-all cursor-pointer touch-manipulation",
                        paymentOptions.paypal
                          ? "bg-primary/5 border-primary/30 shadow-sm"
                          : "bg-card border-border hover:border-border/60"
                      )}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                          <div className="w-5 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
                            P
                          </div>
                        </div>
                        <div>
                          <Label
                            htmlFor="paypal-mobile"
                            className="text-sm font-semibold text-foreground block"
                          >
                            PayPal
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Pay with balance or card
                          </p>
                        </div>
                      </div>
                      <Checkbox
                        id="paypal-mobile"
                        checked={paymentOptions.paypal}
                        onCheckedChange={(checked) =>
                          handlePaymentOptionChange("paypal", checked === true)
                        }
                        className="w-5 h-5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="bank-desktop"
                      checked={paymentOptions.ach}
                      onCheckedChange={(checked) =>
                        handlePaymentOptionChange("ach", checked === true)
                      }
                    />
                    <Label htmlFor="bank-desktop" className="text-sm">
                      Bank (ACH / eCheck)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="credit-desktop"
                      checked={paymentOptions.credit}
                      onCheckedChange={(checked) =>
                        handlePaymentOptionChange("credit", checked === true)
                      }
                    />
                    <Label htmlFor="credit-desktop" className="text-sm">
                      Credit Card
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="applePay-desktop"
                      checked={paymentOptions.applePay}
                      onCheckedChange={(checked) =>
                        handlePaymentOptionChange("applePay", checked === true)
                      }
                    />
                    <Label htmlFor="applePay-desktop" className="text-sm">
                      Apple Pay
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="googlePay-desktop"
                      checked={paymentOptions.googlePay}
                      onCheckedChange={(checked) =>
                        handlePaymentOptionChange("googlePay", checked === true)
                      }
                    />
                    <Label htmlFor="googlePay-desktop" className="text-sm">
                      Google Pay
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="paypal-desktop"
                      checked={paymentOptions.paypal}
                      onCheckedChange={(checked) =>
                        handlePaymentOptionChange("paypal", checked === true)
                      }
                    />
                    <Label htmlFor="paypal-desktop" className="text-sm">
                      PayPal
                    </Label>
                  </div>

                  <div className="flex items-center justify-center">
                    <div className="w-8 h-5 bg-primary rounded text-primary-foreground text-xs flex items-center justify-center font-bold">
                      PAY
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-24">
                <Label className="text-sm font-medium text-foreground mb-8 block">
                  Payment Instructions
                </Label>
                <Textarea
                  placeholder="Tell your customer how you want to get paid..."
                  className="min-h-[90px] bg-background border-border text-sm leading-relaxed"
                />
              </div>
            </div>

            {/* Notes & Memo Section */}
            <div className="bg-card border border-border rounded-lg p-24">
              <div className="flex items-center gap-2 pb-16 border-b border-border">
                <h2 className="text-lg font-semibold text-card-foreground">
                  Additional Information
                </h2>
                <Badge variant="secondaryNew" className="text-xs">
                  Notes & Memo
                </Badge>
              </div>

              <div className="space-y-6 mt-24">
                {/* Notes */}
                <div className="space-y-2">
                  <Label
                    htmlFor="notes"
                    className="text-sm font-semibold text-card-foreground"
                  >
                    Customer Notes
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    These notes will appear on the invoice and be visible to
                    your customer.
                  </p>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="We appreciate your business and look forward to helping you again soon."
                    className="min-h-[100px] resize-none"
                  />
                </div>

                {/* Memo */}
                <div className="space-y-2">
                  <Label
                    htmlFor="memo"
                    className="text-sm font-semibold text-card-foreground"
                  >
                    Internal Memo (Hidden)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Internal notes that will not appear on the invoice but will
                    be visible in statements.
                  </p>
                  <Textarea
                    id="memo"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder="This memo will not show up on your invoice, but will appear on the statement."
                    className="min-h-[80px] resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-card border border-border rounded-lg p-24 mb-20">
              <div className="flex items-center justify-between pb-16 border-b border-border">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-card-foreground">
                    Attachments
                  </h2>
                  <Badge variant="secondaryNew" className="text-xs">
                    Files
                  </Badge>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="select-all" />
                  <Label htmlFor="select-all" className="text-sm font-medium">
                    Select All
                  </Label>
                </div>
              </div>

              <div className="space-y-4 mt-10">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="attachment-1"
                  checked={attachToEmail}
                  onCheckedChange={setAttachToEmail}
                />
                <Label
                  htmlFor="attachment-1"
                  className="text-sm flex items-center gap-2"
                >
                  Attach to email
                </Label>
                {!attachment && (
                  <>
                  <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  className="hidden"
                  />

                  <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 cursor-pointer"
                  onClick={handleChooseImage}
                >
                  Choose Image
                </Button>
                  </>
                )} 
                {attachment && (
                <div className="flex items-center gap-2 text-sm bg-muted px-3 py-2 rounded-md">
                  <span className="text-blue-600 underline">{attachment.name}</span>
                  <span className="text-muted-foreground">
                    {(attachment.size / 1024).toFixed(1)}KB
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={handleRemoveAttachment}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
              </div>
            </div>
              <div className="text-center py-4">
                <span className="text-sm text-muted-foreground">
                  Screenshot
                </span>
              </div>
            </div>
          </div>

          {/* Right Side - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-muted/30 p-16 rounded-lg space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span>Taxable subtotal</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Select sales tax rate</Label>
                  <SingleSelectNew
                      value={taxRate.toString()}
                    onChange={(value) => setTaxRate(parseFloat(value))}
                    options={TaxOptions}
                  />
                </div>

                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-1">
                    Sales tax
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 rounded-full bg-muted"
                    >
                      <span className="text-xs">i</span>
                    </Button>
                  </span>
                  <span>${calculateTaxAmount().toFixed(2)}</span>
                </div>

                <div className="border-t pt-12">
                  <div className="flex justify-between font-semibold">
                    <span>Invoice total</span>
                    <span>${calculateTotal().toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between text-sm">
                  <span>Deposit</span>
                  <span>$0.00</span>
                </div>
              </div>

              <Button
                variant="link"
                className="p-0 h-auto text-sm text-primary"
              >
                Edit totals
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-muted p-16 rounded-lg space-y-4 mt-24">
          <h3 className="font-medium">Professional Actions</h3>
          <div className="grid grid-cols-1 gap-3">
            <Button variant="outline" className="flex items-center gap-2 justify-center" onClick={() => setShowPreview(true)}>
              <Eye className="h-4 w-4" />
              Preview as Client
            </Button>
          </div>
        </div>
         <div className="h-20"></div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-background border-t shadow-lg z-40">
        <div className="max-w-7xl mx-auto p-16">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 sm:flex-none">
                    Print or download
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-8">
                  <div className="space-y-2">
                    <Button variant="ghost" className="w-full justify-start" onClick={() => window.print()}>
                      Print
                    </Button>
                    <Button variant="ghost" className="w-full justify-start" onClick={() => {
                    const element = document.createElement('a');
                    const blob = new Blob([document.documentElement.outerHTML], {
                      type: 'text/html'
                    });
                    element.href = URL.createObjectURL(blob);
                    element.download = `invoice-${invoiceNumber}.html`;
                    element.click();
                  }}>
                      Download
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={e => handleSubmit(e, "draft")} className="flex items-center gap-2 justify-center">
                <Save className="h-4 w-4" />
                Save & Close
              </Button>
              <Button onClick={e => handleSubmit(e, "issued")} className="flex items-center gap-2 justify-center">
                <Send className="h-4 w-4" />
                Review and Send
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Client Preview
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            <div className="space-y-6 p-24">
              <div className="text-center">
                <h1 className="text-3xl font-bold mb-8">INVOICE</h1>
                <p className="text-muted-foreground">Invoice #{invoiceNumber}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold mb-8">Bill To:</h3>
                  <div className="whitespace-pre-line">{clientAddress}</div>
                </div>
                <div>
                  <h3 className="font-semibold mb-8">Invoice Details:</h3>
                  <div className="space-y-1">
                    <div>Date: {invoiceDate ? format(invoiceDate, "MM/dd/yyyy") : "Not set"}</div>
                    <div>Due: {dueDate ? format(dueDate, "MM/dd/yyyy") : "Not set"}</div>
                    <div>Terms: {dueTerm}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Items:</h3>
                {items.map(item => <div key={item.id} className="border-b pb-8">
                    <div className="flex justify-between">
                      <div>
                        <div className="font-medium">{item.productService}</div>
                        <div className="text-sm text-muted-foreground">{item.description}</div>
                      </div>
                      <div className="text-right">
                        <div>${(item.quantity * item.rate).toFixed(2)}</div>
                        <div className="text-sm text-muted-foreground">
                          {item.quantity} × ${item.rate}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="border-t pt-16">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total:</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {notes && <div>
                  <h3 className="font-semibold mb-8">Notes:</h3>
                  <p className="text-sm">{notes}</p>
                </div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={showReviewSend} onOpenChange={setShowReviewSend}>
        <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Email your invoice</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
              
              {/* Left Side - Email Form */}
              <div className="space-y-4 overflow-auto pr-16">
                <div>
                  <Label className="text-sm font-medium">From</Label>
                  <Select defaultValue="procleanings">
                    <SelectTrigger className="mt-4">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="procleanings">ProCleanings.co</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium">To</Label>
                  <div className="flex items-center gap-2 mt-4">
                    <Button variant="link" className="p-0 h-auto text-sm text-primary" onClick={() => setShowReviewCcBcc(!showReviewCcBcc)}>
                      Cc/Bcc
                    </Button>
                    <Input value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="sam.Yun@bogopausa.com" className="flex-1" />
                  </div>
                  {showReviewCcBcc && <div className="mt-8 space-y-2">
                      {reviewCcEmails?.map((email, index) => <div key={`cc-${index}`} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground min-w-[30px]">Cc:</span>
                          <Input value={email} onChange={e => {
                      const newEmails = [...reviewCcEmails];
                      newEmails[index] = e.target.value;
                      setReviewCcEmails(newEmails);
                    }} placeholder="CC email address" className="flex-1" />
                          <Button variant="ghost" size="sm" onClick={() => {
                      setReviewCcEmails(reviewCcEmails.filter((_, i) => i !== index));
                    }} className="h-6 w-6 p-0">
                            <X className="h-3 w-3" />
                          </Button>
                        </div>)}
                      {reviewBccEmails?.map((email, index) => <div key={`bcc-${index}`} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground min-w-[30px]">Bcc:</span>
                          <Input value={email} onChange={e => {
                      const newEmails = [...reviewBccEmails];
                      newEmails[index] = e.target.value;
                      setReviewBccEmails(newEmails);
                    }} placeholder="BCC email address" className="flex-1" />
                          <Button variant="ghost" size="sm" onClick={() => {
                      setReviewBccEmails(reviewBccEmails.filter((_, i) => i !== index));
                    }} className="h-6 w-6 p-0">
                            <X className="h-3 w-3" />
                          </Button>
                        </div>)}
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setReviewCcEmails([...reviewCcEmails, ""])}>
                          + Add CC
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setReviewBccEmails([...reviewBccEmails, ""])}>
                          + Add BCC
                        </Button>
                      </div>
                    </div>}
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox id="send-copy" />
                  <Label htmlFor="send-copy" className="text-sm">Send me a copy</Label>
                </div>

                <div>
                  <Label className="text-sm font-medium">Subject</Label>
                  <Input defaultValue={`New payment request from ${clientName} - Invoice ${invoiceNumber}`} className="mt-1" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="invoice-pdf" defaultChecked />
                    <Label htmlFor="invoice-pdf" className="text-sm">Invoice PDF</Label>
                  </div>
                  {attachToEmail && <div className="flex items-center gap-2">
                      <Checkbox id="attachment-file" defaultChecked />
                      <Label htmlFor="attachment-file" className="text-sm">{attachment?.name}</Label>
                    </div>}
                </div>

                <div>
                  <Label className="text-sm font-medium">Email body</Label>
                  <Textarea defaultValue="Dear Repair & Mainten Sam Yun,

                  We appreciate your business. Please find your invoice details here. Feel free to contact us if you have any questions.

                  Have a great day!" className="mt-4 min-h-[200px]" />
                </div>

                <div className="text-sm text-blue-600 underline cursor-pointer">
                  Manage online delivery settings
                </div>

                <div className="flex gap-2 pt-16 border-t">
                  <Button variant="outline" onClick={() => setShowReviewSend(false)} className="flex-1">
                    Back
                  </Button>
                  <Button onClick={() => {
                  setShowReviewSend(false);
                  toast({
                    title: "Invoice Sent",
                    description: `Invoice #${invoiceNumber} has been sent to ${clientEmail}`
                  });
                }} className="flex-1 bg-green-600 hover:bg-green-700">
                    Send and fund
                  </Button>
                  <Button onClick={() => {
                  setShowReviewSend(false);
                  toast({
                    title: "Invoice Sent",
                    description: `Invoice #${invoiceNumber} has been sent to ${clientEmail}`
                  });
                }} className="flex-1">
                    Send invoice
                  </Button>
                </div>
              </div>

              {/* Right Side - Invoice Preview */}
              <div className="border-l pl-24 overflow-auto">
                <div className="bg-gray-100 p-24 rounded-lg min-h-full">
                  <div className="bg-white p-32 rounded-lg shadow-sm">
                    {/* Invoice Header */}
                    <div className="text-center mb-32">
                      <div className="flex items-center justify-center gap-2 mb-16">
                        <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center">
                          <span className="text-white font-bold text-sm">P</span>
                        </div>
                        <span className="font-bold text-lg">ProCleanings Co.</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        CLEANING • TRAINING AND DEVELOPMENT
                      </div>
                    </div>

                    {/* Ready Banner */}
                    <div className="text-center mb-32">
                      <h2 className="text-2xl font-bold mb-8">Your invoice is ready!</h2>
                      <div className="text-sm text-gray-600 mb-16">INVOICE NO. {invoiceNumber}</div>
                      <div className="text-4xl font-bold mb-24">${calculateTotal().toFixed(2)}</div>
                      
                      <Button className="bg-gray-800 hover:bg-gray-900 text-white px-8 py-2 rounded-full">
                        View and pay
                      </Button>
                    </div>

                    {/* Payment Options */}
                    <div className="flex justify-center gap-2 mb-32">
                      <div className="w-8 h-5 bg-black text-white text-xs flex items-center justify-center rounded">Pay</div>
                      <div className="w-12 h-5 bg-blue-600 text-white text-xs flex items-center justify-center rounded">VISA</div>
                      <div className="w-8 h-5 bg-red-500 text-white text-xs flex items-center justify-center rounded">MC</div>
                      <div className="w-12 h-5 bg-orange-500 text-white text-xs flex items-center justify-center rounded">DISC</div>
                      <div className="w-12 h-5 bg-blue-500 text-white text-xs flex items-center justify-center rounded">AMEX</div>
                      <div className="w-8 h-5 bg-gray-400 text-white text-xs flex items-center justify-center rounded">BANK</div>
                      <div className="w-8 h-5 bg-blue-700 text-white text-xs flex items-center justify-center rounded">PP</div>
                      <div className="w-12 h-5 bg-gray-600 text-white text-xs flex items-center justify-center rounded">venmo</div>
                    </div>

                    {/* Message */}
                    <div className="text-center text-sm text-gray-600 mb-32">
                      We appreciate your business and look forward to helping you again soon.
                    </div>

                    {/* Customer Info */}
                    <div className="text-sm">
                      <div className="font-medium">Dear Repair & Mainten Sam Yun,</div>
                      <div className="mt-4 text-gray-600">
                        We appreciate your business. Please find your invoice details here. Feel free to contact us if you have any questions.
                      </div>
                      <div className="mt-16">Have a great day!</div>
                      <div className="mt-16 font-medium">ProCleanings Co.</div>
                    </div>

                    {/* Footer */}
                    <div className="mt-32 text-xs text-gray-500 text-center border-t pt-16">
                      <div>ProCleanings Co.</div>
                      <div>47 Woodland Ave Montclair, NJ 07042 US</div>
                      <div className="mt-32 d-flex flex-center">
                        <span className="text-blue-600">+1#5814095141</span> • 
                        <span className="text-blue-600 mh-10">repairpro33@gmail.com</span> • 
                        <span className="text-blue-600 ml-10">www.verdantpros.com</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default InvoiceCreate;
